import { useState, useEffect } from "react";
import {
  Trophy, Plus, Edit, Trash2, Search, X, Save, Image as ImageIcon, 
  Loader2, Users, Calendar, Star, StarOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const TOURNAMENT_COLORS = [
  "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B",
  "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#6366F1"
];

// Tournament Card Component
function TournamentCard({ tournament: t, onEdit, onDelete, onRecalculate }) {
  return (
    <div className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow">
      <div className="h-2" style={{ backgroundColor: t.color }} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-brand-text">{t.name}</h3>
            <p className="text-sm text-gray-500">{t.season}</p>
          </div>
          {t.is_featured && <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />}
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge className={t.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
            {t.status === "active" ? "Activo" : "Finalizado"}
          </Badge>
          <Badge className={t.division === "primera" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}>
            {t.division === "primera" ? "1ra División" : "2da División"}
          </Badge>
          <Badge variant="outline">
            <Users className="h-3 w-3 mr-1" />
            {t.team_ids?.length || 0} equipos
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1" onClick={() => onEdit(t)}>
            <Edit className="h-4 w-4 mr-1" />Editar
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRecalculate(t.tournament_id)} title="Recalcular tabla">
            <Calendar className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" className="text-red-500" onClick={() => onDelete(t.tournament_id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TournamentForm({ tournament, teams, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: tournament?.name || "",
    season: tournament?.season || "2026 Clausura",
    logo_url: tournament?.logo_url || "",
    color: tournament?.color || "#10B981",
    status: tournament?.status || "active",
    tournament_type: tournament?.tournament_type || "league",
    division: tournament?.division || "primera",
    team_ids: tournament?.team_ids || [],
    points_win: tournament?.points_win ?? 3,
    points_draw: tournament?.points_draw ?? 1,
    points_loss: tournament?.points_loss ?? 0,
    is_featured: tournament?.is_featured || false,
    featured_order: tournament?.featured_order || 0,
  });
  const [saving, setSaving] = useState(false);

  // Filter teams by division
  const filteredTeams = teams.filter(t => !formData.division || t.division === formData.division || !t.division);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.season) {
      toast.error("Nombre y temporada son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleTeam = (teamId) => {
    setFormData((prev) => ({
      ...prev,
      team_ids: prev.team_ids.includes(teamId)
        ? prev.team_ids.filter((id) => id !== teamId)
        : [...prev.team_ids, teamId],
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto">
      {/* Name & Season */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Nombre del Torneo *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Liga Nacional"
            className="mt-1"
            required
          />
        </div>
        <div>
          <Label>Temporada *</Label>
          <Input
            value={formData.season}
            onChange={(e) => setFormData({ ...formData, season: e.target.value })}
            placeholder="Ej: 2026 Clausura"
            className="mt-1"
            required
          />
        </div>
      </div>

      {/* Color */}
      <div>
        <Label>Color del Torneo</Label>
        <div className="flex gap-2 mt-2 flex-wrap">
          {TOURNAMENT_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className={`w-8 h-8 rounded-full border-2 transition-all ${formData.color === color ? "border-gray-800 scale-110" : "border-transparent"}`}
              style={{ backgroundColor: color }}
              onClick={() => setFormData({ ...formData, color })}
            />
          ))}
          <Input
            type="color"
            value={formData.color}
            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            className="w-8 h-8 p-0 border-0"
          />
        </div>
      </div>

      {/* Type & Status & Division */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>División *</Label>
          <Select value={formData.division} onValueChange={(v) => {
            setFormData({ ...formData, division: v, team_ids: [] }); // Reset teams when division changes
          }}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="primera">Primera División</SelectItem>
              <SelectItem value="segunda">Segunda División</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tipo</Label>
          <Select value={formData.tournament_type} onValueChange={(v) => setFormData({ ...formData, tournament_type: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="league">Liga</SelectItem>
              <SelectItem value="cup">Copa</SelectItem>
              <SelectItem value="group_knockout">Grupos + Eliminación</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Estado</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="finished">Finalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Points Config */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Pts Victoria</Label>
          <Input type="number" value={formData.points_win} onChange={(e) => setFormData({ ...formData, points_win: parseInt(e.target.value) || 0 })} className="mt-1" />
        </div>
        <div>
          <Label>Pts Empate</Label>
          <Input type="number" value={formData.points_draw} onChange={(e) => setFormData({ ...formData, points_draw: parseInt(e.target.value) || 0 })} className="mt-1" />
        </div>
        <div>
          <Label>Pts Derrota</Label>
          <Input type="number" value={formData.points_loss} onChange={(e) => setFormData({ ...formData, points_loss: parseInt(e.target.value) || 0 })} className="mt-1" />
        </div>
      </div>

      {/* Featured */}
      <div className="flex items-center justify-between py-4 border-t border-b">
        <div>
          <Label>Torneo Destacado</Label>
          <p className="text-sm text-gray-500">Mostrar en la sección principal de Deportes</p>
        </div>
        <Switch checked={formData.is_featured} onCheckedChange={(c) => setFormData({ ...formData, is_featured: c })} />
      </div>
      
      {/* Featured Order */}
      {formData.is_featured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <Label className="flex items-center gap-2 text-amber-800">
            <Star className="h-4 w-4" />
            Orden de aparición (1-10)
          </Label>
          <p className="text-xs text-amber-600 mb-3">El torneo con orden 1 aparece primero</p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setFormData({ ...formData, featured_order: num })}
                className={`w-10 h-10 rounded-lg font-bold text-sm transition-all ${
                  formData.featured_order === num
                    ? "bg-primary text-white shadow-lg scale-110"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-primary hover:text-primary"
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Teams Selection */}
      <div>
        <Label>Equipos Participantes ({formData.team_ids.length}) - {formData.division === "primera" ? "Primera División" : "Segunda División"}</Label>
        <p className="text-xs text-gray-500 mb-2">Solo se muestran equipos de {formData.division === "primera" ? "primera" : "segunda"} división</p>
        <div className="mt-2 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
          {filteredTeams.length === 0 ? (
            <p className="col-span-2 text-center text-gray-400 py-4">No hay equipos en esta división</p>
          ) : (
            filteredTeams.map((team) => (
              <label key={team.team_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${formData.team_ids.includes(team.team_id) ? "bg-primary/10" : ""}`}>
                <input
                  type="checkbox"
                  checked={formData.team_ids.includes(team.team_id)}
                  onChange={() => toggleTeam(team.team_id)}
                  className="rounded"
                />
                {team.logo_url && <img src={getImageUrl(team.logo_url)} alt="" className="w-6 h-6 object-contain" />}
                <span className="text-sm truncate">{team.name}</span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={saving}>
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : <><Save className="h-4 w-4 mr-2" />Guardar</>}
        </Button>
      </div>
    </form>
  );
}

export default function AdminTorneos() {
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, limit]);

  const fetchData = async () => {
    try {
      const [tournamentsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/sports/tournaments?skip=${(page - 1) * limit}&limit=${limit}`),
        axios.get(`${API}/sports/teams?is_active=true&limit=200`),
      ]);
      setTournaments(tournamentsRes.data.items || []);
      setTotal(tournamentsRes.data.total || 0);
      setTeams(teamsRes.data.items || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (editingTournament) {
      await axios.put(`${API}/sports/tournaments/${editingTournament.tournament_id}`, data);
      toast.success("Torneo actualizado");
    } else {
      await axios.post(`${API}/sports/tournaments`, data);
      toast.success("Torneo creado");
    }
    fetchData();
    setEditingTournament(null);
    setShowForm(false);
  };

  const handleDelete = async (tournamentId) => {
    if (!window.confirm("¿Eliminar este torneo y todos sus datos (jornadas, partidos)?")) return;
    try {
      await axios.delete(`${API}/sports/tournaments/${tournamentId}`);
      toast.success("Torneo eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const recalculateStandings = async (tournamentId) => {
    try {
      await axios.post(`${API}/sports/standings/${tournamentId}/recalculate`);
      toast.success("Tabla recalculada");
    } catch (error) {
      toast.error("Error al recalcular");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
              <Trophy className="h-6 w-6 text-primary" />
              Torneos
            </h1>
            <p className="text-brand-textSecondary">Gestiona torneos y competencias</p>
          </div>
          <Button onClick={() => { setEditingTournament(null); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Torneo
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : tournaments.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No hay torneos</h3>
            <Button className="mt-4" onClick={() => { setEditingTournament(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-2" />Crear Torneo
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Sort tournaments: Active first, then by division (primera, segunda) */}
            {(() => {
              // Separate active and inactive
              const activeTournaments = tournaments.filter(t => t.status === "active");
              const inactiveTournaments = tournaments.filter(t => t.status !== "active");
              
              // Sort each group by division (primera first, then segunda)
              const sortByDivision = (a, b) => {
                if (a.division === "primera" && b.division !== "primera") return -1;
                if (a.division !== "primera" && b.division === "primera") return 1;
                return 0;
              };
              
              activeTournaments.sort(sortByDivision);
              inactiveTournaments.sort(sortByDivision);
              
              // Group by division for display
              const activePrimera = activeTournaments.filter(t => t.division === "primera");
              const activeSegunda = activeTournaments.filter(t => t.division !== "primera");
              const inactivePrimera = inactiveTournaments.filter(t => t.division === "primera");
              const inactiveSegunda = inactiveTournaments.filter(t => t.division !== "primera");
              
              const handleEdit = (t) => { setEditingTournament(t); setShowForm(true); };
              
              return (
                <>
                  {/* Active Tournaments Section */}
                  {activeTournaments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                          <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                          Torneos Activos
                        </h2>
                        <Badge className="bg-green-100 text-green-700">{activeTournaments.length}</Badge>
                      </div>
                      
                      {/* Primera División - Activos */}
                      {activePrimera.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              <Trophy className="h-3 w-3 mr-1" />
                              Primera División
                            </Badge>
                            <span className="text-sm text-gray-500">{activePrimera.length} torneo{activePrimera.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {activePrimera.map((t) => (
                              <TournamentCard 
                                key={t.tournament_id} 
                                tournament={t} 
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onRecalculate={recalculateStandings}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Segunda División - Activos */}
                      {activeSegunda.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-orange-100 text-orange-700 text-xs">
                              <Trophy className="h-3 w-3 mr-1" />
                              Segunda División
                            </Badge>
                            <span className="text-sm text-gray-500">{activeSegunda.length} torneo{activeSegunda.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {activeSegunda.map((t) => (
                              <TournamentCard 
                                key={t.tournament_id} 
                                tournament={t} 
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onRecalculate={recalculateStandings}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Inactive Tournaments Section */}
                  {inactiveTournaments.length > 0 && (
                    <div className="pt-6 border-t">
                      <div className="flex items-center gap-3 mb-4">
                        <h2 className="text-lg font-bold text-gray-500 flex items-center gap-2">
                          <div className="h-3 w-3 bg-gray-400 rounded-full" />
                          Torneos Finalizados
                        </h2>
                        <Badge className="bg-gray-100 text-gray-600">{inactiveTournaments.length}</Badge>
                      </div>
                      
                      {/* Primera División - Inactivos */}
                      {inactivePrimera.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-blue-50 text-blue-500 text-xs border border-blue-200">
                              <Trophy className="h-3 w-3 mr-1" />
                              Primera División
                            </Badge>
                            <span className="text-sm text-gray-400">{inactivePrimera.length} torneo{inactivePrimera.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
                            {inactivePrimera.map((t) => (
                              <TournamentCard 
                                key={t.tournament_id} 
                                tournament={t} 
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onRecalculate={recalculateStandings}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Segunda División - Inactivos */}
                      {inactiveSegunda.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className="bg-orange-50 text-orange-400 text-xs border border-orange-200">
                              <Trophy className="h-3 w-3 mr-1" />
                              Segunda División
                            </Badge>
                            <span className="text-sm text-gray-400">{inactiveSegunda.length} torneo{inactiveSegunda.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
                            {inactiveSegunda.map((t) => (
                              <TournamentCard 
                                key={t.tournament_id} 
                                tournament={t} 
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onRecalculate={recalculateStandings}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTournament ? "Editar Torneo" : "Nuevo Torneo"}</DialogTitle>
            </DialogHeader>
            <TournamentForm tournament={editingTournament} teams={teams} onSave={handleSave} onClose={() => { setShowForm(false); setEditingTournament(null); }} />
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="torneos-pagination"
          label="torneos"
        />
    </AdminLayout>
  );
}
